import React, { useCallback } from 'react'
import { useMemo } from 'react'
import classNames from 'classnames'
import { Else, If, Then } from 'react-if'
import { StarFilledIcon } from '@/atoms/Icons/StarFilledIcon'
import { CaptionLG, LabelSM } from '@/atoms/Text'
import { PIF_NAME_IN_CREDITS_MIN_USD } from '@/constants/pif'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { useLocale } from '@/utils/LocaleUtil'

const nameInCreditsBorderGradient = 'linear-gradient(159deg, #DFB695 14%, #F3D0B6 41%, #DEA67D 62%, #B98268 89%)'

// Helper component to render Caption with Name-in-Credits styles (new PIF layout only)
const NameInCreditsChip = ({
  label,
  isSelected,
  isEligible,
}: {
  label: string
  isSelected: boolean
  isEligible: boolean
}) => {
  const caption = (
    <LabelSM
      weight="semibold"
      className={classNames(
        'rounded-[50px] border-[1.5px] relative text-xs md:text-sm md:px-[18px] py-[10px] px-4 flex items-center justify-center select-none',
        {
          'border-copper border-[1.5px] !text-core-gray-800 bg-transparent': isSelected && isEligible,
          '!bg-core-gray-950 !border-core-gray-950 !text-white': isSelected && !isEligible,
          '!bg-core-gray-300 !border-core-gray-400 !text-core-gray-800': !isSelected,
        },
      )}
    >
      {isEligible && !isSelected && (
        <span className="pointer-events-none absolute -right-1.5 -top-1 z-10 mr-1 flex h-4 w-4 items-center justify-center rounded-full border-[2px] border-white bg-warm-03">
          <StarFilledIcon size={10} color={'white'} />
        </span>
      )}
      {label}
    </LabelSM>
  )

  return isEligible && isSelected ? (
    <div className="rounded-full p-[1px]" style={{ backgroundImage: nameInCreditsBorderGradient }}>
      {caption}
    </div>
  ) : (
    caption
  )
}

interface TheatricalExpressPriceOptionsProps {
  offers: Offer[]
  onClick: (offer: Offer) => void
  selectedOffer: Offer | undefined
  shouldShowPeopleImpacted?: boolean
  shouldUseEqualColumns?: boolean
  newPifLayout?: boolean
  pifNameInCreditsEnabled?: boolean
}

export const TheatricalExpressPriceOptions: React.FC<TheatricalExpressPriceOptionsProps> = ({
  offers,
  onClick,
  selectedOffer,
  shouldShowPeopleImpacted = true,
  shouldUseEqualColumns = false,
  newPifLayout = false,
  pifNameInCreditsEnabled = false,
}) => {
  const { locale } = useLocale()
  const { setNameInCredits } = usePayItForwardOfferData()

  const getReach = useCallback(
    (offer: Offer) => {
      const num = getPeopleImpacted(offer)
      return new Intl.NumberFormat(locale, { notation: 'compact' }).format(num)
    },
    [locale],
  )

  const filteredOffers = useMemo(() => {
    return offers?.filter((offer) => offer.price <= 750000) || []
  }, [offers])

  const handleOptionClick = useCallback(
    (offer: Offer) => {
      const amount = getPriceForCurrency(offer.price, offer.currency)
      if (amount < PIF_NAME_IN_CREDITS_MIN_USD) {
        setNameInCredits('')
      }
      onClick(offer)
    },
    [onClick, setNameInCredits],
  )

  return (
    <>
      <If condition={shouldUseEqualColumns}>
        <Then>
          <div
            className={classNames('grid', newPifLayout ? 'gap-2 grid-cols-5 mt-4 md:mt-6' : 'gap-1 grid-cols-4 mt-6')}
          >
            {filteredOffers.map((offer) => {
              const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
              const isEligible = getPriceForCurrency(offer.price, offer.currency) >= PIF_NAME_IN_CREDITS_MIN_USD
              const isSelected = offer.id === selectedOffer?.id
              const label = shouldShowPeopleImpacted ? getReach(offer) : price
              return (
                <div
                  key={offer?.id}
                  className="flex-basis-auto shrink-0 grow cursor-pointer text-center"
                  onClick={() => handleOptionClick(offer)}
                >
                  {newPifLayout ? (
                    <NameInCreditsChip
                      label={label}
                      isSelected={isSelected}
                      isEligible={pifNameInCreditsEnabled && isEligible}
                    />
                  ) : (
                    <CaptionLG
                      weight="bold"
                      className={classNames(
                        isSelected
                          ? 'border-core-gray-800 bg-core-gray-800 text-white'
                          : 'border-core-gray-200 bg-core-gray-100',
                        'rounded-lg border-2 p-2 select-none',
                      )}
                    >
                      {label}
                    </CaptionLG>
                  )}
                </div>
              )
            })}
          </div>
        </Then>
        <Else>
          <div className="mt-6 flex flex-wrap gap-1">
            {filteredOffers.map((offer) => {
              const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
              const isEligible = getPriceForCurrency(offer.price, offer.currency) >= PIF_NAME_IN_CREDITS_MIN_USD
              const isSelected = offer.id === selectedOffer?.id
              const label = shouldShowPeopleImpacted ? getReach(offer) : price
              return (
                <div
                  key={offer?.id}
                  className="flex-basis-auto shrink-0 grow cursor-pointer text-center"
                  onClick={() => handleOptionClick(offer)}
                >
                  {newPifLayout ? (
                    <NameInCreditsChip
                      label={label}
                      isSelected={isSelected}
                      isEligible={pifNameInCreditsEnabled && isEligible}
                    />
                  ) : (
                    <CaptionLG
                      weight="bold"
                      className={classNames(
                        isSelected
                          ? 'border-core-gray-800 bg-core-gray-800 text-white'
                          : 'border-core-gray-200 bg-core-gray-100',
                        'rounded-lg border-2 p-2 select-none',
                      )}
                    >
                      {label}
                    </CaptionLG>
                  )}
                </div>
              )
            })}
          </div>
        </Else>
      </If>
    </>
  )
}
